import axios from 'axios';
import { graphql, useStaticQuery } from 'gatsby';
import { and } from 'ramda';
import React, { FC, useContext } from 'react';
import { useAsync } from 'react-use';

import CenteredGridItem from '@ui/molecules/CenteredGridItem';

import NextStepsStep, { INextStepsStepPageContent } from '@ui/templates/NextStepsStep';

import { keysToCamel } from '@utils/butter';
import { ApiEndpoints } from '@utils/endpoints';
import { doesNotExist } from '@utils/general';

import { getAuthToken } from '@providers/AuthProvider';
import FlowContext from '@providers/FlowContextProvider';

import { IFlowStepProps } from '@folx-types/flow-steps';

interface INextStepsStepPageProps extends IFlowStepProps {}

const NextStepsStepPage: FC<INextStepsStepPageProps> = ({ path, stepKey }) => {
	const { pageContent } = useStaticQuery(graphql`
		{
			pageContent: butterPage(slug: { eq: "mental-health-next-steps" }) {
				page_header
				page_description
				alt_description
				get_care_label
				get_care_href
				explore_label
				explore_href
				appointment_error_header
				appointment_error_info
				reschedule_appointment_button
			}
		}
	`);

	const { existingFormData, handleFormSubmission, handleNavBack } = useContext(FlowContext);

	const refinedContent: INextStepsStepPageContent = keysToCamel(
		pageContent
	) as INextStepsStepPageContent;

	const {
		loading: qualtricsLoading,
		error: qualtricsError,
		value: qualtricsData,
	} = useAsync(async () => {
		const authorizationToken = await getAuthToken();
		const assignSurveyData = await axios.get<{
			distributionLink: { distributionLink: string; surveyStatus: string };
		}>(`${ApiEndpoints.surveys}/assign-survey/MH_SHORT`, {
			headers: {
				authorizationToken,
			},
			responseType: 'json',
		});

		return assignSurveyData?.data.distributionLink;
	}, []);

	return (
		<CenteredGridItem>
			<NextStepsStep
				loading={
					qualtricsLoading ||
					doesNotExist(pageContent) ||
					and(doesNotExist(qualtricsData), doesNotExist(qualtricsError))
				}
				pageContent={refinedContent}
				path={path}
				stepKey={stepKey}
				existingFormData={existingFormData}
				handleFormSubmission={handleFormSubmission}
				handleNavBack={handleNavBack}
				qualtrics={qualtricsData}
			/>
		</CenteredGridItem>
	);
};

export default NextStepsStepPage;
